/**
 * Weight over time as a small inline SVG line chart, with the same numbers as a table for screen
 * readers and anyone who'd rather read them. Estimates are hollow and left off the line; a projection
 * is dashed and labeled so it can never pass for a weight.
 */
import { useId, useState } from "react";
import { formatDate, formatNumber } from "../../../web/lib/format";
import { t } from "../../../web/lib/i18n";
import { cx } from "../../../web/ui/kit";
import { contextLabel } from "./fmt";
import type { WeightContext } from "../shared/types";

export interface ChartPoint { on: string; value: number; context?: WeightContext; estimate?: boolean; flagged?: boolean }

const W = 600;
const H = 220;
const PAD = { left: 52, right: 16, top: 16, bottom: 30 };

const time = (d: string) => Date.parse(`${d}T12:00:00Z`);

function niceStep(range: number): number {
  const raw = range / 3;
  const pow = 10 ** Math.floor(Math.log10(Math.max(raw, 1)));
  return [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? pow * 10;
}

export function WeightChart({ points, unit, projected }: { points: ChartPoint[]; unit: string; projected?: { on: string; value: number } | null }) {
  const id = useId();
  const [table, setTable] = useState(false);
  const all = [...points, ...(projected ? [{ on: projected.on, value: projected.value }] : [])];
  if (!points.length) return null;
  const minV = Math.min(...all.map((p) => p.value));
  const maxV = Math.max(...all.map((p) => p.value));
  const step = niceStep(Math.max(maxV - minV, maxV * 0.1, 10));
  const lo = Math.max(0, Math.floor(minV / step) * step);
  const hi = Math.ceil(maxV / step) * step + (maxV % step === 0 ? step : 0);
  const t0 = time(all[0].on);
  const t1 = Math.max(time(all.at(-1)!.on), t0 + 86_400_000);
  const x = (d: string) => PAD.left + ((time(d) - t0) / (t1 - t0)) * (W - PAD.left - PAD.right);
  const y = (v: number) => PAD.top + (1 - (v - lo) / (hi - lo || 1)) * (H - PAD.top - PAD.bottom);
  const ticks: number[] = [];
  for (let v = lo; v <= hi + 0.001; v += step) ticks.push(v);
  const actual = points.filter((p) => !p.estimate);
  const line = actual.map((p, i) => `${i ? "L" : "M"}${x(p.on).toFixed(1)},${y(p.value).toFixed(1)}`).join(" ");
  const last = actual.at(-1);
  const first = points[0];
  const end = points.at(-1)!;
  const desc = points.length === 1
    ? t("One weight: {{value}} {{unit}} on {{date}}.", { value: formatNumber(first.value, 0), unit, date: formatDate(first.on) })
    : t("{{count}} weights from {{first}} {{unit}} on {{from}} to {{last}} {{unit}} on {{to}}.", { count: points.length, first: formatNumber(first.value, 0), from: formatDate(first.on), last: formatNumber(end.value, 0), to: formatDate(end.on), unit });

  return (
    <figure className="perf-chart">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-labelledby={`${id}-t ${id}-d`} preserveAspectRatio="xMidYMid meet">
        <title id={`${id}-t`}>{t("Weight history")}</title>
        <desc id={`${id}-d`}>{desc}{projected ? ` ${t("Projected about {{value}} {{unit}} today, from recent gain. Not a weight.", { value: formatNumber(projected.value, 0), unit })}` : ""}</desc>
        {ticks.map((v) => (
          <g key={v} className="perf-chart-grid">
            <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} />
            <text x={PAD.left - 8} y={y(v)} dy="0.35em" textAnchor="end">{formatNumber(v, 0)}</text>
          </g>
        ))}
        <text className="perf-chart-axis" x={PAD.left} y={H - 8} textAnchor="start">{formatDate(all[0].on)}</text>
        <text className="perf-chart-axis" x={W - PAD.right} y={H - 8} textAnchor="end">{formatDate(all.at(-1)!.on)}</text>
        {actual.length > 1 && <path className="perf-chart-line" d={line} />}
        {projected && last && <path className="perf-chart-projection" d={`M${x(last.on)},${y(last.value)} L${x(projected.on)},${y(projected.value)}`} />}
        {points.map((p, i) => (
          <circle key={`${p.on}-${i}`} cx={x(p.on)} cy={y(p.value)} r={p.context === "birth" || p.context === "weaning" || p.context === "yearling" ? 6 : 4.5}
            className={cx("perf-chart-dot", p.estimate && "is-estimate", p.flagged && "is-flagged", p.context && `is-${p.context}`)}>
            <title>{`${formatDate(p.on)}: ${formatNumber(p.value, 0)} ${unit}${p.context ? `, ${contextLabel(p.context).toLowerCase()}` : ""}${p.estimate ? `, ${t("estimated")}` : ""}`}</title>
          </circle>
        ))}
        {projected && <circle cx={x(projected.on)} cy={y(projected.value)} r={5} className="perf-chart-dot is-projected"><title>{t("Projected, not weighed")}</title></circle>}
      </svg>
      <figcaption className="perf-chart-legend">
        <span><i className="perf-key perf-key-dot" aria-hidden />{t("Weighed")}</span>
        {points.some((p) => p.estimate) && <span><i className="perf-key perf-key-hollow" aria-hidden />{t("Estimated weight")}</span>}
        {projected && <span><i className="perf-key perf-key-dash" aria-hidden />{t("Projected, not weighed")}</span>}
        <button type="button" className="link" aria-expanded={table} onClick={() => setTable(!table)}>{table ? t("Hide the numbers") : t("Show the numbers")}</button>
      </figcaption>
      {table && (
        <table className="perf-table">
          <caption className="perf-sr">{t("Weight history")}</caption>
          <thead><tr><th scope="col">{t("Date")}</th><th scope="col" className="num">{t("Weight")}</th><th scope="col">{t("Weighing")}</th></tr></thead>
          <tbody>
            {[...points].reverse().map((p, i) => (
              <tr key={`${p.on}-${i}`}><td>{formatDate(p.on)}</td><td className="num">{formatNumber(p.value, 0)} {unit}</td><td>{[contextLabel(p.context), p.estimate ? t("estimated") : null].filter(Boolean).join(", ")}</td></tr>
            ))}
            {projected && <tr className="is-projected"><td>{formatDate(projected.on)}</td><td className="num">{formatNumber(projected.value, 0)} {unit}</td><td>{t("Projected, not weighed")}</td></tr>}
          </tbody>
        </table>
      )}
    </figure>
  );
}
